import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  MobileStepper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { enrollmentService, MemberBasicInfo } from '../services/enrollment.service';
import {
  EnrollmentFormData,
  ValidationErrors,
  initialEnrollmentFormData,
} from '../types/enrollment.types';
import {
  PersonalInfoStep,
  ContactInfoStep,
  EmergencyContactStep,
  ReviewSummaryStep,
  ConsentStep,
  SignatureStep,
  SignatureStepRef,
} from '../components/enrollment/steps';
import DuplicateMemberDialog from '../components/DuplicateMemberDialog';

// Simplified flow: Only emergency contact is skippable
const SKIPPABLE_STEPS = [2]; // Emergency Contact only

export default function EnrollmentWizard() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<EnrollmentFormData>(initialEnrollmentFormData);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [memberId, setMemberId] = useState<string | null>(null);
  const signatureRef = useRef<SignatureStepRef>(null);

  // Duplicate checking state
  const [duplicateMember, setDuplicateMember] = useState<MemberBasicInfo | null>(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  // Simplified steps - removed health questionnaire steps (2-7)
  const steps = [
    { key: 'personal', label: t('step.personal') },
    { key: 'contact', label: t('step.contact') },
    { key: 'emergency', label: t('step.emergency') },
    { key: 'review', label: t('step.review') },
    { key: 'consent', label: t('step.consent') },
    { key: 'signature', label: t('step.signature') },
  ];

  const handleChange = <K extends keyof EnrollmentFormData>(field: K) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: EnrollmentFormData[K] } }
  ) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleCheckboxChange = <K extends keyof EnrollmentFormData>(field: K) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.checked }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleArrayChange = <K extends keyof EnrollmentFormData>(
    field: K,
    value: EnrollmentFormData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSignatureChange = (data: string) => {
    setFormData((prev) => ({ ...prev, signatureData: data }));
    setErrors((prev) => ({ ...prev, signatureData: undefined }));
  };

  const handleSkipSignatureChange = (skip: boolean) => {
    setFormData((prev) => ({ ...prev, skipSignature: skip }));
    // Clear signature error when skipping
    if (skip) {
      setErrors((prev) => ({ ...prev, signatureData: undefined }));
    }
  };

  // Check phone availability on blur
  const handlePhoneBlur = useCallback(async () => {
    const phone = formData.phone.trim();
    if (!phone || phone.length < 8) return;

    // Validate format first
    const digitsOnly = phone.replace(/[\s\-+()]/g, '');
    const isValidHK = /^(852)?[2-9]\d{7}$/.test(digitsOnly);
    if (!isValidHK) return;

    setIsCheckingPhone(true);
    try {
      const { available } = await enrollmentService.checkPhoneAvailability(phone);
      if (!available) {
        setErrors((prev) => ({ ...prev, phone: t('error.duplicatePhone') }));
        // Try to get member info
        const memberInfo = await enrollmentService.getMemberByPhone(phone);
        if (memberInfo) {
          setDuplicateMember(memberInfo);
          setShowDuplicateDialog(true);
        }
      } else {
        // Clear phone error if it was a duplicate error
        setErrors((prev) => {
          if (prev.phone === t('error.duplicatePhone')) {
            return { ...prev, phone: undefined };
          }
          return prev;
        });
      }
    } catch (error) {
      console.error('Phone check failed:', error);
    } finally {
      setIsCheckingPhone(false);
    }
  }, [formData.phone, t]);

  // Check email availability on blur
  const handleEmailBlur = useCallback(async () => {
    const email = formData.email.trim();
    if (!email) return;

    // Validate format first
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;

    setIsCheckingEmail(true);
    try {
      const { available } = await enrollmentService.checkEmailAvailability(email);
      if (!available) {
        setErrors((prev) => ({ ...prev, email: t('error.duplicateEmail') }));
      } else {
        // Clear email error if it was a duplicate error
        setErrors((prev) => {
          if (prev.email === t('error.duplicateEmail')) {
            return { ...prev, email: undefined };
          }
          return prev;
        });
      }
    } catch (error) {
      console.error('Email check failed:', error);
    } finally {
      setIsCheckingEmail(false);
    }
  }, [formData.email, t]);

  // Handle closing duplicate dialog
  const handleCloseDuplicateDialog = () => {
    setShowDuplicateDialog(false);
  };

  const validateStep = (step: number): boolean => {
    const newErrors: ValidationErrors = {};

    switch (step) {
      case 0: // Personal Info
        if (!formData.firstName.trim()) newErrors.firstName = t('validation.required');
        if (!formData.lastName.trim()) newErrors.lastName = t('validation.required');
        if (!formData.dateOfBirth) newErrors.dateOfBirth = t('validation.required');
        if (!formData.gender) newErrors.gender = t('validation.required');
        // HK ID validation (optional, but validate format if provided)
        if (formData.hkIdPrefix && !/^[A-Z]{1,2}\d{3,4}$/i.test(formData.hkIdPrefix)) {
          newErrors.hkIdPrefix = t('validation.hkIdFormat');
        }
        break;

      case 1: // Contact Info
        if (!formData.phone.trim()) {
          newErrors.phone = t('validation.required');
        } else {
          // Hong Kong phone: 8 digits, optionally with +852 prefix
          const digitsOnly = formData.phone.replace(/[\s\-+()]/g, '');
          const isValidHK = /^(852)?[2-9]\d{7}$/.test(digitsOnly);
          if (!isValidHK) {
            newErrors.phone = t('validation.phone');
          }
        }
        // Email is optional, but validate format if provided
        if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          newErrors.email = t('validation.email');
        }
        break;

      case 2: // Emergency Contact - all optional
        // No required fields
        break;

      case 3: // Review - read-only, no validation
        break;

      case 4: // Consent - required checkboxes
        if (!formData.consentTerms) newErrors.consentTerms = t('validation.required');
        if (!formData.consentPrivacy) newErrors.consentPrivacy = t('validation.required');
        if (!formData.consentHealth) newErrors.consentHealth = t('validation.required');
        break;

      case 5: // Signature
        // Only require signature if not skipping (staff enrolled on behalf)
        if (!formData.skipSignature && signatureRef.current?.isEmpty()) {
          newErrors.signatureData = t('signature.required');
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      if (activeStep === steps.length - 1) {
        // Capture signature before submitting (only if not skipping)
        if (!formData.skipSignature && signatureRef.current && !signatureRef.current.isEmpty()) {
          const signatureData = signatureRef.current.getSignatureData();
          setFormData((prev) => ({ ...prev, signatureData }));
        }
        handleSubmit();
      } else {
        setActiveStep((prev) => prev + 1);
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSkip = () => {
    if (SKIPPABLE_STEPS.includes(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Capture signature if not already captured (only if not skipping)
      let signatureData = formData.signatureData;
      if (!formData.skipSignature && signatureRef.current && !signatureRef.current.isEmpty() && !signatureData) {
        signatureData = signatureRef.current.getSignatureData();
      }

      const result = await enrollmentService.enrollMember({
        ...formData,
        signatureData: formData.skipSignature ? '' : signatureData,
      });

      setMemberId(result.memberId);
      setActiveStep(steps.length);
    } catch (error: unknown) {
      console.error('Enrollment error:', error);

      // Map backend error messages to i18n keys
      const errorMessage = error instanceof Error ? error.message : '';

      if (errorMessage.includes('phone number already exists') || errorMessage.includes('phone')) {
        setSubmitError(t('error.duplicatePhone'));
        // Also set the field error and show dialog
        setErrors((prev) => ({ ...prev, phone: t('error.duplicatePhone') }));
        // Try to get member info for dialog
        if (formData.phone) {
          enrollmentService.getMemberByPhone(formData.phone).then((memberInfo) => {
            if (memberInfo) {
              setDuplicateMember(memberInfo);
              setShowDuplicateDialog(true);
            }
          });
        }
      } else if (errorMessage.includes('email already exists') || errorMessage.includes('email')) {
        setSubmitError(t('error.duplicateEmail'));
        setErrors((prev) => ({ ...prev, email: t('error.duplicateEmail') }));
      } else if (errorMessage.includes('Session expired') || errorMessage.includes('401')) {
        setSubmitError(t('error.sessionExpired'));
      } else if (errorMessage.includes('Server error') || errorMessage.includes('500')) {
        setSubmitError(t('error.serverError'));
      } else if (errorMessage.includes('Unable to connect') || errorMessage.includes('network')) {
        setSubmitError(t('error.networkError'));
      } else {
        setSubmitError(t('error.tryAgain'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartOver = () => {
    setFormData(initialEnrollmentFormData);
    setActiveStep(0);
    setErrors({});
    setSubmitError(null);
    setMemberId(null);
    signatureRef.current?.clear();
  };

  const stepProps = {
    formData,
    errors,
    onChange: handleChange,
    onCheckboxChange: handleCheckboxChange,
    onArrayChange: handleArrayChange,
    onPhoneBlur: handlePhoneBlur,
    onEmailBlur: handleEmailBlur,
    isCheckingPhone,
    isCheckingEmail,
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <PersonalInfoStep {...stepProps} />;
      case 1:
        return <ContactInfoStep {...stepProps} />;
      case 2:
        return <EmergencyContactStep {...stepProps} />;
      case 3:
        return <ReviewSummaryStep formData={formData} simplified />;
      case 4:
        return <ConsentStep {...stepProps} />;
      case 5:
        return (
          <SignatureStep
            ref={signatureRef}
            {...stepProps}
            onSignatureChange={handleSignatureChange}
            onSkipSignatureChange={handleSkipSignatureChange}
          />
        );
      default:
        return null;
    }
  };

  // Success screen
  if (activeStep === steps.length) {
    return (
      <Paper
        sx={{
          p: 4,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main' }} />
        <Typography variant="h4" color="success.main">
          {t('success.title')}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {t('success.message')}
        </Typography>
        {memberId && (
          <Paper sx={{ p: 3, bgcolor: 'primary.light' }}>
            <Typography color="white">{t('success.memberId')}</Typography>
            <Typography variant="h4" color="white" fontWeight="bold">
              {memberId}
            </Typography>
          </Paper>
        )}
        {/* Pass to staff instruction */}
        <Paper
          sx={{
            p: 3,
            bgcolor: 'warning.light',
            width: '100%',
            maxWidth: 400,
            border: '2px solid',
            borderColor: 'warning.main',
          }}
        >
          <Typography variant="h5" fontWeight="bold" color="warning.dark" gutterBottom>
            {t('success.passToStaff')}
          </Typography>
          <Typography variant="body1" color="warning.dark">
            {t('success.passToStaffSubtext')}
          </Typography>
        </Paper>
        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleStartOver}
          >
            {t('button.startOver')}
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => window.close()}
            color="secondary"
          >
            {t('button.closeWindow')}
          </Button>
        </Box>
      </Paper>
    );
  }

  const canSkip = SKIPPABLE_STEPS.includes(activeStep);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      {/* Desktop Stepper */}
      {!isMobile ? (
        <Stepper
          activeStep={activeStep}
          sx={{
            mb: 4,
            '& .MuiStepLabel-label': {
              fontSize: '0.75rem',
            },
          }}
          alternativeLabel
        >
          {steps.map((step) => (
            <Step key={step.key}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      ) : (
        // Mobile progress indicator
        <Box sx={{ mb: 2, px: 2 }}>
          <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
            {t('step.progress', { current: activeStep + 1, total: steps.length })}
          </Typography>
          <MobileStepper
            variant="progress"
            steps={steps.length}
            position="static"
            activeStep={activeStep}
            sx={{
              bgcolor: 'transparent',
              '& .MuiLinearProgress-root': { width: '100%' },
            }}
            nextButton={<Box />}
            backButton={<Box />}
          />
        </Box>
      )}

      <Paper sx={{ p: { xs: 2, sm: 4 }, flex: 1 }}>
        {submitError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {submitError}
          </Alert>
        )}

        {renderStepContent(activeStep)}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={activeStep === 0 || isSubmitting}
            size="large"
            startIcon={<KeyboardArrowLeft />}
          >
            {t('button.back')}
          </Button>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {canSkip && (
              <Button
                variant="text"
                onClick={handleSkip}
                disabled={isSubmitting}
                size="large"
              >
                {t('button.skip')}
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={isSubmitting}
              size="large"
              sx={{ minWidth: 150 }}
              endIcon={activeStep !== steps.length - 1 && <KeyboardArrowRight />}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : activeStep === steps.length - 1 ? (
                t('button.submit')
              ) : (
                t('button.next')
              )}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Duplicate Member Dialog */}
      <DuplicateMemberDialog
        open={showDuplicateDialog}
        onClose={handleCloseDuplicateDialog}
        memberInfo={duplicateMember}
      />
    </Box>
  );
}
